// BI — selezione clienti (singoli o gruppi salvati) per calcolare le statistiche solo su di loro
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, X, Users, Save, Trash2, FolderOpen, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const keyOf = (m) => m.customer_id || m.name.toLowerCase();

export default function BiCustomerFocus({ open, onClose, selection, onApply }) {
  const [draft, setDraft] = useState(selection);
  const [q, setQ] = useState("");
  const [cands, setCands] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadGroups = () => axios.get(`${API}/admin/bi/groups`).then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { if (open) { setDraft(selection); setQ(""); setCands([]); setGroupName(""); setEditing(null); loadGroups(); } }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setCands([]); return undefined; }
    const t = setTimeout(() => axios.get(`${API}/admin/bi/exclusions/candidates`, { params: { q } }).then((r) => setCands(r.data)).catch(() => setCands([])), 250);
    return () => clearTimeout(t);
  }, [q]);

  const inDraft = new Set(draft.map(keyOf));
  const add = (c) => { if (!inDraft.has(keyOf(c))) setDraft([...draft, { name: c.name, customer_id: c.customer_id || null }]); setQ(""); setCands([]); };
  const remove = (m) => setDraft(draft.filter((x) => keyOf(x) !== keyOf(m)));
  const applyGroup = (g) => { setDraft(g.members); setGroupName(g.name); setEditing(g.id); };

  const saveGroup = async () => {
    const name = groupName.trim();
    if (!name || !draft.length) return;
    setBusy(true);
    try {
      const r = editing ? await axios.put(`${API}/admin/bi/groups/${editing}`, { name, members: draft }) : await axios.post(`${API}/admin/bi/groups`, { name, members: draft });
      toast.success(`Gruppo "${name}" salvato`);
      setEditing(r.data.id); setGroupName(r.data.name);
      await loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); } finally { setBusy(false); }
  };
  const deleteGroup = async (g) => {
    try { await axios.delete(`${API}/admin/bi/groups/${g.id}`); toast.success(`Gruppo "${g.name}" eliminato`); if (editing === g.id) { setEditing(null); setGroupName(""); } await loadGroups(); } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="bi-focus-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[#8A1C2A]" /> Statistiche per cliente</DialogTitle>
          <p className="text-xs text-stone-500">Scegli uno o più clienti: tutta la BI (vendite, prodotti, ordini, finanza) viene calcolata solo su di loro. Puoi salvare la selezione come gruppo da riusare.</p>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente da aggiungere (min 2 lettere)..." className="h-12 w-full pl-9 pr-3 rounded-xl border border-stone-300 text-sm" data-testid="bi-focus-search" />
          </div>
          {cands.length > 0 && (
            <div className="border border-stone-200 rounded-xl divide-y divide-stone-100 max-h-56 overflow-y-auto" data-testid="bi-focus-candidates">
              {cands.map((c) => {
                const already = inDraft.has(keyOf(c));
                return (
                  <button key={keyOf(c)} type="button" disabled={already} onClick={() => add(c)} className="w-full text-left flex items-center gap-3 px-3 py-2.5 min-h-[48px] hover:bg-stone-50 disabled:opacity-50" data-testid={`bi-focus-candidate-${c.name}`}>
                    <div className="flex-1 min-w-0"><p className="font-medium text-stone-900 truncate">{c.name}</p><p className="text-[11px] text-stone-500">{c.origin}{c.customer_id ? " · in anagrafica" : ""}</p></div>
                    {already ? <span className="text-[11px] text-stone-400">già selezionato</span> : <Plus className="w-4 h-4 text-[#8A1C2A]" />}
                  </button>
                );
              })}
            </div>
          )}
          {q.trim().length >= 2 && !cands.length && <p className="text-xs text-stone-500 italic">Nessun cliente trovato</p>}

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Selezionati ({draft.length})</p>
              {draft.length > 0 && <button type="button" onClick={() => { setDraft([]); setEditing(null); setGroupName(""); }} className="text-xs font-semibold text-stone-500 underline" data-testid="bi-focus-clear">Svuota</button>}
            </div>
            {!draft.length && <p className="text-sm text-stone-500 italic" data-testid="bi-focus-empty">Nessun cliente selezionato: le statistiche includono tutti i clienti.</p>}
            <div className="flex flex-wrap gap-2" data-testid="bi-focus-chips">
              {draft.map((m) => (
                <span key={keyOf(m)} className="inline-flex items-center gap-1 pl-3 pr-1 h-9 rounded-full bg-[#8A1C2A]/10 text-[#8A1C2A] text-sm font-medium" data-testid={`bi-focus-chip-${m.name}`}>
                  <span className="max-w-[180px] truncate">{m.name}</span>
                  <button type="button" onClick={() => remove(m)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#8A1C2A]/15" aria-label={`Rimuovi ${m.name}`} data-testid={`bi-focus-chip-remove-${m.name}`}><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            {draft.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={editing ? "Nome del gruppo" : "Salva selezione come gruppo…"} className="h-11 flex-1 px-3 rounded-xl border border-stone-300 text-sm" data-testid="bi-focus-group-name" />
                <button type="button" onClick={saveGroup} disabled={busy || !groupName.trim()} className="h-11 px-3 rounded-xl bg-stone-900 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="bi-focus-group-save"><Save className="w-4 h-4" /> {editing ? "Aggiorna" : "Salva"}</button>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Gruppi salvati ({groups.length})</p>
            {!groups.length && <p className="text-sm text-stone-500 italic" data-testid="bi-focus-groups-empty">Nessun gruppo: seleziona i clienti e salvali con un nome.</p>}
            <div className="divide-y divide-stone-100 border border-stone-200 rounded-xl overflow-hidden">
              {groups.map((g) => (
                <div key={g.id} className={`flex items-center gap-2 px-3 py-2 min-h-[52px] ${editing === g.id ? "bg-[#8A1C2A]/5" : ""}`} data-testid={`bi-focus-group-${g.id}`}>
                  <button type="button" onClick={() => applyGroup(g)} className="flex-1 min-w-0 text-left" data-testid={`bi-focus-group-apply-${g.id}`}>
                    <p className="font-medium text-stone-900 truncate flex items-center gap-1.5"><FolderOpen className="w-4 h-4 text-[#8A1C2A] shrink-0" />{g.name}</p>
                    <p className="text-[11px] text-stone-500 truncate">{g.members.length} clienti · {g.members.slice(0, 3).map((m) => m.name).join(", ")}{g.members.length > 3 ? "…" : ""}</p>
                  </button>
                  <button type="button" onClick={() => applyGroup(g)} className="w-10 h-10 rounded-xl flex items-center justify-center text-stone-600 hover:bg-stone-100" aria-label="Modifica gruppo" data-testid={`bi-focus-group-edit-${g.id}`}><Pencil className="w-4 h-4" /></button>
                  <button type="button" onClick={() => deleteGroup(g)} className="w-10 h-10 rounded-xl flex items-center justify-center text-red-700 hover:bg-red-50" aria-label="Elimina gruppo" data-testid={`bi-focus-group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-1">
            {selection.length > 0 && <button type="button" onClick={() => { onApply([], null); onClose(); }} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700" data-testid="bi-focus-reset">Tutti i clienti</button>}
            <button type="button" onClick={() => { onApply(draft, editing ? (groups.find((g) => g.id === editing)?.name || groupName.trim() || null) : null); onClose(); }} className="h-11 px-4 rounded-xl bg-[#8A1C2A] text-white text-sm font-semibold" data-testid="bi-focus-apply">
              {draft.length ? `Applica (${draft.length} client${draft.length === 1 ? "e" : "i"})` : "Applica · tutti i clienti"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
